<script setup lang="ts">
import { ref, computed, reactive, onMounted, onUnmounted } from 'vue'
import { api, type EventEntry } from '../api/client'

const events = ref<EventEntry[]>([])
let timer: ReturnType<typeof setInterval> | null = null

async function poll() {
  try {
    events.value = await api.events()
  } catch { /* ignore */ }
}

onMounted(() => {
  poll()
  timer = setInterval(poll, 10000) // poll every 10s
})

onUnmounted(() => {
  if (timer) clearInterval(timer)
})

// Group consecutive events. Default rule: identical type+message (e.g.
// repeated polls). Special-case types in COLLAPSE_BY_TYPE_ALONE: collapse
// consecutive events regardless of message.
//  - `position`: multi-user runs produce one entry per user, all fired at
//    the snapshot tick; only the latest matters by default.
//  - `signal_transition`: intraday flapping produces alternating
//    "SELL → BUY" / "BUY → SELL" messages every few minutes, which the
//    identical-message rule never groups. The operator only needs the
//    latest state; the flap count survives as the +N badge.
interface EventGroup {
  latest: EventEntry
  older: EventEntry[]
}

const COLLAPSE_BY_TYPE_ALONE = new Set(['position', 'signal_transition'])

const groupedEvents = computed<EventGroup[]>(() => {
  const groups: EventGroup[] = []
  for (const ev of events.value) {
    const last = groups[groups.length - 1]
    const sameGroup = last && last.latest.type === ev.type && (
      COLLAPSE_BY_TYPE_ALONE.has(ev.type)
        ? true
        : last.latest.message === ev.message
    )
    if (sameGroup) {
      last!.older.push(ev)
    } else {
      groups.push({ latest: ev, older: [] })
    }
  }
  return groups
})

const expandedGroups = reactive<Set<number>>(new Set())
function toggleGroup(i: number) {
  if (expandedGroups.has(i)) expandedGroups.delete(i)
  else expandedGroups.add(i)
}

const typeIcon: Record<string, string> = {
  nav: '\u{1F4B0}',
  // 2026-08-06: JH and Gold NAV strikes were falling through to the
  // default bullet — visually inconsistent with BGA's 💰 despite being
  // the same class of event. All three fund pollers now share the
  // money-bag icon. Underlying types differ because each poller writes
  // to its own event type (BGA=nav via V008 legacy contract, others
  // fund-scoped names added later).
  jh_nav_written: '\u{1F4B0}',
  gold_nav_written: '\u{1F4B0}',
  gge_nav_written: '\u{1F4B0}',
  gge_official_nav_landed: '\u{1F4B0}',
  // GGE promotion Phase 4: failure/staleness events get the same red-alert
  // weight as their gold/data-refresh siblings (health-monitor RED-UI rule).
  gge_nav_hl_failed: '\u{1F6A8}',
  gge_nav_ft_failed: '\u{1F6A8}',
  gge_nav_postgres_failed: '\u{1F6A8}',
  gge_nav_capture_gave_up: '\u{1F6A8}',
  gge_snapshot_stale: '\u{1F6A8}',
  holdings: '\u{1F4CA}',
  // 2026-08-13 (paolo): post-09:00-cutoff residual cash published by
  // DealingPlanTickService.RunPlanCloseAsync \u2014 the spendable remainder
  // after the day's dealt BUYs, also held in Redis for OTP sizing.
  residual_cash: '\u{1F4B7}',
  rebalance: '\u21BB',
  zscore: '\u{1F4C8}',
  system: '\u{2699}\uFE0F',
  data_refresh: '\u{2705}',
  // 2026-05-29: dedicated resolution event paired with the 🚨
  // data_refresh_failed event. Emitted by GbpUsdLateNightRetryService
  // when the 04:00 UTC catch-up succeeds. White-on-green icon to
  // visually clear the earlier red alert.
  data_refresh_resolved: '\u{2705}',
  data_refresh_failed: '\u{1F6A8}',
  av_key_lapsed: '\u{1F6A8}',
  execution_failed: '\u{1F6A8}',
  // 2026-08-06: HealthMonitorService emits every staleness alert as
  // type='health_alert' (shadow_stale, signal_stale, holdings_daily_stale,
  // etc). Was rendering as default grey — invisibility bug that let a
  // 6-day AV outage go unnoticed. Red 🚨 lifts it to the same visual
  // weight as execution_failed / av_key_lapsed.
  health_alert: '\u{1F6A8}',
  // 2026-11-01 health alert cadence: reminder while open, resolved when cleared.
  health_reminder: '\u26A0\uFE0F',
  health_resolved: '\u{2705}',
  // Per-user WhatsApp routing (2026-09-28): admin-only operator copies of
  // per-user account alerts (never WhatsApp-relayed) — as loud as health_alert.
  account_health_attention: '\u{1F6A8}',
  hl_login_attention: '\u{1F6A8}',
  // Signal-health RAG transitions (spec 2026-10-23); gated funds use admin-only <fund>_signal_health_* twins.
  signal_health_red: '\u{1F6A8}',
  signal_health_cleared: '\u{2705}',
  japan_signal_health_red: '\u{1F6A8}',
  japan_signal_health_cleared: '\u{2705}',
  charteris_signal_health_red: '\u{1F6A8}',
  charteris_signal_health_cleared: '\u{2705}',
  amati_signal_health_red: '\u{1F6A8}',
  amati_signal_health_cleared: '\u{2705}',
  execution: '⚡',
  // 2026-07-22: daily 16:05-NY BGA EOD summary (synth NAV + strike FVA)
  // emitted by signal_core._eod_summary_loop.
  eod: '\u{1F4D5}',
}

function fmtTime(ts: string) {
  try {
    const d = new Date(ts)
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  } catch { return ts }
}

function fmtDate(ts: string) {
  try {
    const d = new Date(ts)
    const today = new Date()
    if (d.toDateString() === today.toDateString()) return 'Today'
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday'
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
  } catch { return '' }
}
</script>

<template>
  <section class="event-log card">
    <h2>Event Log</h2>
    <div v-if="events.length === 0" class="empty">No events recorded</div>
    <div v-else class="event-list">
      <template v-for="(g, gi) in groupedEvents" :key="gi">
        <div class="event-row" :class="'type-' + g.latest.type">
          <span class="ev-icon">{{ typeIcon[g.latest.type] || '\u{2022}' }}</span>
          <span class="ev-time" :title="g.latest.timestamp">{{ fmtDate(g.latest.timestamp) }} {{ fmtTime(g.latest.timestamp) }}</span>
          <span class="ev-type">{{ g.latest.type }}</span>
          <span class="ev-msg">{{ g.latest.message }}</span>
          <span v-if="g.latest.detail" class="ev-detail" :title="g.latest.detail">{{ g.latest.detail }}</span>
          <span v-if="g.older.length" class="ev-collapse" @click.stop="toggleGroup(gi)">{{ expandedGroups.has(gi) ? '\u25B2' : '' }} +{{ g.older.length }}</span>
        </div>
        <template v-if="g.older.length && expandedGroups.has(gi)">
          <div v-for="(ev, ei) in g.older" :key="'o' + ei" class="event-row ev-indented" :class="'type-' + ev.type">
            <span class="ev-icon"></span>
            <span class="ev-time" :title="ev.timestamp">{{ fmtDate(ev.timestamp) }} {{ fmtTime(ev.timestamp) }}</span>
            <span class="ev-type">{{ ev.type }}</span>
            <span class="ev-msg">{{ ev.message }}</span>
            <span v-if="ev.detail" class="ev-detail" :title="ev.detail">{{ ev.detail }}</span>
          </div>
        </template>
      </template>
    </div>
  </section>
</template>

<style scoped>
.event-log {
}

.event-log h2 {
  margin: 0 0 0.5rem 0;
  font-size: 0.85rem;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.empty {
  color: #555;
  font-style: italic;
  font-size: 0.85rem;
}

.event-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.event-row {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.2rem 0;
  font-size: 0.8rem;
  border-bottom: 1px solid #1a1a1a;
}

.ev-icon {
  flex-shrink: 0;
  width: 1.2em;
  text-align: center;
}

.ev-time {
  flex-shrink: 0;
  color: #666;
  font-family: monospace;
  font-size: 0.75rem;
  min-width: 110px;
}

.ev-type {
  flex-shrink: 0;
  color: #888;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  min-width: 60px;
}

.ev-msg {
  color: #ccc;
}

.ev-detail {
  color: #555;
  font-size: 0.75rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 300px;
}

.type-nav .ev-msg { color: #4caf50; }
.type-holdings .ev-msg { color: #2196f3; }
.type-rebalance .ev-msg { color: #9ec5ff; font-weight: 600; }
.type-rebalance .ev-type { color: #3a7bd5; }
.type-rebalance .ev-icon { color: #3a7bd5; }
.type-zscore .ev-msg { color: #ff9800; }
.type-system .ev-msg { color: #9e9e9e; }
.type-data_refresh .ev-msg { color: #4caf50; font-weight: 700; }
/* Bright green resolution event — visually pairs with the red failure
   event so the operator can see the issue cleared at a glance. */
.type-data_refresh_resolved .ev-msg { color: #4caf50; font-weight: 700; }
.type-data_refresh_resolved .ev-type { color: #4caf50; }
.type-data_refresh_failed .ev-msg { color: #f44336; font-weight: 700; }
.type-data_refresh_failed .ev-type { color: #f44336; }
.type-av_key_lapsed .ev-msg { color: #f44336; font-weight: 700; }
.type-av_key_lapsed .ev-type { color: #f44336; }
.type-execution_failed .ev-msg { color: #f44336; font-weight: 700; }
.type-execution_failed .ev-type { color: #f44336; }
/* 2026-08-06: all health_alert entries (stale feeds, missed schedules,
   pipeline stalls) render red — match execution_failed weight. See the
   typeIcon entry above for the invisibility incident that motivated
   this. */
.type-health_alert .ev-msg { color: #f44336; font-weight: 700; }
.type-health_alert .ev-type { color: #f44336; }
.type-health_alert .ev-icon { color: #f44336; }
/* Per-user WhatsApp routing (2026-09-28): operator copies of per-user
   account alerts (admin-only, never relayed) render as loudly. */
.type-account_health_attention .ev-msg,
.type-hl_login_attention .ev-msg,
.type-signal_health_red .ev-msg,
.type-japan_signal_health_red .ev-msg,
.type-charteris_signal_health_red .ev-msg,
.type-amati_signal_health_red .ev-msg { color: #f44336; font-weight: 700; }
.type-account_health_attention .ev-type,
.type-hl_login_attention .ev-type,
.type-signal_health_red .ev-type,
.type-japan_signal_health_red .ev-type,
.type-charteris_signal_health_red .ev-type,
.type-amati_signal_health_red .ev-type { color: #f44336; }
.type-account_health_attention .ev-icon,
.type-hl_login_attention .ev-icon,
.type-signal_health_red .ev-icon,
.type-japan_signal_health_red .ev-icon,
.type-charteris_signal_health_red .ev-icon,
.type-amati_signal_health_red .ev-icon { color: #f44336; }
/* Successful executions — yellow so operator can spot real-money actions
   among the high-volume routine event stream. */
.type-execution .ev-msg { color: #ffd54f; font-weight: 700; }
.type-execution .ev-type { color: #ffd54f; }
.type-execution .ev-icon { color: #ffd54f; }

.ev-collapse {
  flex-shrink: 0;
  margin-left: auto;
  color: #666;
  font-size: 0.7rem;
  cursor: pointer;
  padding: 0 4px;
  border-radius: 3px;
  background: #222;
}
.ev-collapse:hover { color: #aaa; background: #2a2a2a; }

.ev-indented {
  opacity: 0.6;
  padding-left: 1.2em;
  border-left: 2px solid #333;
  margin-left: 0.6em;
}

/* ---- Mobile responsive ---- */
@media (max-width: 768px) {
  .event-row {
    flex-wrap: wrap;
    padding: 0.4rem 0;
    gap: 0.25rem;
  }

  .ev-icon {
    width: auto;
  }

  .ev-time {
    min-width: auto;
    font-size: 0.7rem;
  }

  .ev-type {
    min-width: auto;
    font-size: 0.65rem;
  }

  .ev-msg {
    width: 100%;
    padding-left: 1.45em;
    font-size: 0.8rem;
  }

  .ev-detail {
    width: 100%;
    padding-left: 1.45em;
    max-width: none;
    white-space: normal;
    word-break: break-word;
    font-size: 0.7rem;
  }

  .event-log {
  }

  .event-log h2 {
    font-size: 0.8rem;
  }
}
</style>
